/* eslint-disable */
import { API_ORIGIN as DISCORD_API_BASE } from "../config/api";

async function discordRequest(path, options = {}) {
  const token = localStorage.getItem("mg_token");
  return fetch(`${DISCORD_API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
}

export const DISCORD_SORT_OPTIONS = [
  { value: "members", label: "Members" },
  { value: "online", label: "Online" },
  { value: "online_pct", label: "Online %" },
  { value: "boosts", label: "Boosts" },
  { value: "server_name", label: "Name" },
];

export async function fetchDiscordServers(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, value);
  });
  const qs = search.toString();
  const res = await discordRequest(`/api/discord/servers${qs ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error(`Discord servers request failed (HTTP ${res.status})`);
  return res.json();
}

export async function fetchDiscordServerHistory(serverId, days = 30) {
  const res = await discordRequest(
    `/api/discord/server/${encodeURIComponent(serverId)}/history?days=${days}`
  );
  if (!res.ok) throw new Error(`Discord history request failed (HTTP ${res.status})`);
  return res.json();
}

// The bubble views need the whole filtered set at once. Fetch page one to learn
// the total, then pull the remaining pages in parallel.
export async function fetchDiscordServersBulk(params = {}, max = 1500) {
  const pageSize = 2000;
  const base = { ...params, sort: "members", order: "desc" };

  const first = await fetchDiscordServers({ ...base, limit: pageSize, offset: 0 });
  const total = Number(first.total) || 0;
  const items = Array.isArray(first.items) ? first.items : [];
  const wanted = Math.min(total, max);

  const offsets = [];
  for (let offset = items.length; offset < wanted; offset += pageSize) offsets.push(offset);

  if (offsets.length) {
    const pages = await Promise.all(
      offsets.map((offset) =>
        fetchDiscordServers({
          ...base,
          limit: Math.min(pageSize, wanted - offset),
          offset,
        })
      )
    );
    // Keep member-sorted order by appending pages in offset order.
    pages.forEach((p) => items.push(...(Array.isArray(p.items) ? p.items : [])));
  }

  return { items: items.slice(0, max), total };
}

export async function updateDiscordServer(serverId, patch) {
  const res = await discordRequest(
    `/api/admin/discord/server/${encodeURIComponent(serverId)}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
  if (!res.ok) throw new Error(`Discord update failed (HTTP ${res.status})`);
  return res.json();
}

export async function refreshDiscordServer(serverId) {
  const res = await discordRequest(
    `/api/admin/discord/server/${encodeURIComponent(serverId)}/refresh`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error(`Refresh failed (HTTP ${res.status})`);
  return res.json();
}

export async function resolveDiscordInvites(urls) {
  const res = await discordRequest(
    `/api/admin/discord/resolve`,
    { method: "POST", body: JSON.stringify({ urls }) }
  );
  if (!res.ok) throw new Error(`Resolve failed (HTTP ${res.status})`);
  return res.json();
}

export async function importDiscordServers(servers) {
  const res = await discordRequest(
    `/api/admin/discord/import`,
    { method: "POST", body: JSON.stringify({ servers }) }
  );
  if (!res.ok) throw new Error(`Import failed (HTTP ${res.status})`);
  return res.json();
}

// Matches the "7.83M" / "311.6K" / "639" compact style used elsewhere in mindGAME.
export function formatCompactNumber(value) {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) return `${(num / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (abs >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return num.toLocaleString();
}

export function formatPercent(value) {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${num.toFixed(1)}%`;
}

// The view returns categories/keywords/features as pipe-delimited text
// (e.g. "Memes|Gaming"), occasionally JSON, or an actual array.
export function parseDiscordList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  const str = String(value).trim();
  if (!str) return [];
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
  } catch {
    // not JSON, fall through to delimiter parsing
  }
  const delimiter = str.includes("|") ? "|" : ",";
  return str.split(delimiter).map((s) => s.trim()).filter(Boolean);
}
